var m = require('mithril');
var headingElement = require('../../components/heading');
var Point = require('../models/point');
var buttonElement = require('../../components/button');
var enumerationEditor = require('./enumerationEditor');
var loaders = require('../../layout/components/loaders');
var isAggregatePoint = require('../isAggregatePoint');

var VISIBLE_SUGGESTIONS = 5;
var MAX_SUGGESTIONS = 50;
var DEFAULT_MULTI_POINT_WRITE_TITLE = 'Set Multi-Point Write Values';

function normalizeRidForUrl(rid) {
  if (rid && typeof rid === 'object') {
    rid = rid['@rid'];
  }
  if (typeof rid !== 'string' || !rid) {
    return null;
  }
  return rid[0] === '#' ? rid.slice(1) : rid;
}

function normalizeAliasList(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map(function (item) { return String(item || '').trim(); })
      .filter(function (item) { return item.length > 0; });
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map(function (item) { return item.trim(); })
      .filter(function (item) { return item.length > 0; });
  }
  return [];
}

function pickReadablePrefixAlias(prefix) {
  if (!prefix) return '';
  var name = String(prefix.name || '').trim();
  if (name) return name;
  var aliases = normalizeAliasList(prefix.aliases);
  var bestAlpha = '';
  var bestAny = '';
  aliases.forEach(function (alias) {
    if (!alias) return;
    if (alias.length > bestAny.length) {
      bestAny = alias;
    }
    if (/^[A-Za-z]+$/.test(alias) && alias.length > bestAlpha.length) {
      bestAlpha = alias;
    }
  });
  if (bestAlpha) return bestAlpha;
  if (bestAny) return bestAny;
  return prefix.symbol ? String(prefix.symbol).trim() : '';
}

function buildUnitOptions(units, prefixes) {
  var options = [];
  var optionKinds = {};
  var seen = {};
  var prefixSymbols = [];

  (Array.isArray(prefixes) ? prefixes : []).forEach(function (prefix) {
    var alias = pickReadablePrefixAlias(prefix);
    if (!alias) return;
    if (prefixSymbols.indexOf(alias) === -1) {
      prefixSymbols.push(alias);
    }
  });
  
  function addOption(value, kind) {
    var trimmed = String(value || '').trim();
    if (!trimmed || seen[trimmed]) return;
    seen[trimmed] = true;
    options.push({ value: trimmed, kind: kind || '' });
    if (!optionKinds[trimmed]) {
      optionKinds[trimmed] = kind || '';
    }
  }

  (Array.isArray(units) ? units : []).forEach(function (unit) {
    if (!unit) return;
    var aliases = normalizeAliasList(unit.aliases || unit.aliasText);
    var names = normalizeAliasList(unit.name);
    var tokens = names.concat(aliases);
    tokens.forEach(function (token) {
      addOption(token, unit.kind || '');
      if (/^[A-Za-z]/.test(token)) {
        prefixSymbols.forEach(function (prefix) {
          addOption(prefix + token, unit.kind || '');
        });
      }
    });
  });

  ['int', 'bool', 'str'].forEach(function (unit) {
    addOption(unit, 'special');
  });

  return { options: options, optionKinds: optionKinds, prefixSymbols: prefixSymbols };
}

function normalizeOptionValue(value) {
  return String(value || '').trim().toLowerCase();
}

function formatKindLabel(kind) {
  if (!kind) return '';
  var text = String(kind);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function renderBubble(text, spanClass, textClass) {
  return m(`span.br2.nowrap.dib.ph1.pv1.mr1${spanClass}`, [
    m('span.' + (textClass || 'moon-gray'), text)
  ]);
}

function splitUnitSegment(segment, prefixSymbols) {
  var value = String(segment || '').trim();
  if (!value) return { prefix: '', unit: '' };
  var sorted = (prefixSymbols || []).slice().sort(function (a, b) { return b.length - a.length; });
  for (var i = 0; i < sorted.length; i += 1) {
    var prefix = sorted[i];
    if (!prefix) continue;
    if (value.indexOf(prefix) === 0 && value.length > prefix.length) {
      return { prefix: prefix, unit: value.slice(prefix.length) };
    }
  }
  return { prefix: '', unit: value };
}

function renderUnitBubbles(value, prefixSymbols) {
  var parts = String(value || '').split('/');
  var nodes = [];
  parts.forEach(function (segment, index) {
    var split = splitUnitSegment(segment, prefixSymbols);
    if (split.prefix) {
      nodes.push(renderBubble(split.prefix, '.bg-mid-gray', 'white'));
    }
    if (split.unit) {
      nodes.push(renderBubble(split.unit, '.bg-mid-gray', 'white'));
    }
    if (index < parts.length - 1) {
      nodes.push(m('span.mh1.moon-gray', '/'));
    }
  });
  return nodes;
}

function formatOptionLabel(value, kind, prefixSymbols) {
  var label = renderUnitBubbles(value, prefixSymbols);
  if (kind) {
    label = label.concat([
      m('span.light-silver.f5', ' (' + formatKindLabel(kind) + ')')
    ]);
  }
  return label;
}

function filterBaseOptions(options, query, limit, allowEmpty) {
  var value = normalizeOptionValue(query);
  if (!value) {
    return allowEmpty ? (options || []).slice(0, limit) : [];
  }
  var exact = [];
  var starts = [];
  (Array.isArray(options) ? options : []).forEach(function (opt) {
    var text = normalizeOptionValue(opt && opt.value);
    if (!text) return;
    if (text === value) {
      exact.push(opt);
    } else if (text.indexOf(value) === 0) {
      starts.push(opt);
    }
  });
  return exact.concat(starts).slice(0, limit);
}

function filterUnitOptions(options, optionKinds, query, limit) {
  var raw = String(query || '');
  if (!raw.trim()) return [];
  var slashIndex = raw.indexOf('/');
  if (slashIndex === -1) {
    return filterBaseOptions(options, raw, limit, false);
  }

  var numeratorQuery = raw.slice(0, slashIndex).trim();
  var denominatorQuery = raw.slice(slashIndex + 1).trim();
  var partLimit = Math.max(5, Math.floor(limit / 2));
  var numerators = filterBaseOptions(options, numeratorQuery, partLimit, true);
  var denominators = filterBaseOptions(options, denominatorQuery, partLimit, true);
  var combos = [];
  var seen = {};

  numerators.forEach(function (num) {
    denominators.forEach(function (den) {
      var comboValue = num.value + '/' + den.value;
      if (seen[comboValue]) return;
      seen[comboValue] = true;
      combos.push({
        value: comboValue,
        kind: optionKinds && optionKinds[comboValue] ? optionKinds[comboValue] : ''
      });
    });
  });

  return combos.slice(0, limit);
}

function getDatasetFromUsedOnPath(path) {
  if (!Array.isArray(path)) return null;

  for (var index = path.length - 1; index >= 0; index -= 1) {
    var record = path[index];
    if (record && record['@class'] === 'Dataset' && record['@rid']) {
      return record;
    }
  }

  return null;
}

function formatUsedOnPath(path) {
  if (!Array.isArray(path)) return '';

  return path
    .filter(function (record) {
      return record && record['@class'] && (record.name || record['@class']);
    })
    .map(function (record) {
      if (record.name === 'root') return 'Home';
      return record.name || record['@class'];
    })
    .join(' / ');
}

function buildWriteGroupOptions(pointRecord, currentWriteGroup) {
  var options = [];
  var seen = {};

  ((pointRecord && pointRecord.usedOn) || []).forEach(function (path) {
    var dataset = getDatasetFromUsedOnPath(path);
    if (!dataset || seen[dataset['@rid']]) return;

    seen[dataset['@rid']] = true;
    options.push({
      value: dataset['@rid'],
      label: formatUsedOnPath(path)
    });
  });

  if (currentWriteGroup && !seen[currentWriteGroup]) {
    options.unshift({
      value: currentWriteGroup,
      label: 'Configured dataset (' + currentWriteGroup + ')'
    });
  }

  return options;
}

function writeGroupTitleValue(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function loadWriteGroupTitle(ctrl, writeGroupRid) {
  if (!writeGroupRid) {
    ctrl.writeGroupTitleRid = null;
    ctrl.writeGroupTitle = '';
    ctrl.writeGroupTitleOriginal = '';
    ctrl.writeGroupTitleDirty = false;
    ctrl.writeGroupTitleLoading = false;
    ctrl.writeGroupTitleError = null;
    return;
  }

  if (ctrl.writeGroupTitleRid === writeGroupRid || ctrl.writeGroupTitleLoadingRid === writeGroupRid) return;

  ctrl.writeGroupTitleRid = writeGroupRid;
  ctrl.writeGroupTitleLoadingRid = writeGroupRid;
  ctrl.writeGroupTitle = '';
  ctrl.writeGroupTitleOriginal = '';
  ctrl.writeGroupTitleDirty = false;
  ctrl.writeGroupTitleLoading = true;
  ctrl.writeGroupTitleError = null;

  loaders.requestWithLoader({
    loader: 'background',
    method: 'GET',
    url: '/api/v1/dataset/' + normalizeRidForUrl(writeGroupRid),
  })
  .then(function (dataset) {
    if (ctrl.writeGroupTitleRid !== writeGroupRid) return;
    var title = writeGroupTitleValue(dataset && dataset.writeGroupTitle);
    ctrl.writeGroupTitle = title;
    ctrl.writeGroupTitleOriginal = title;
    ctrl.writeGroupTitleDirty = false;
    ctrl.writeGroupTitleLoading = false;
    ctrl.writeGroupTitleLoadingRid = null;
    m.redraw();
  })
  .catch(function (error) {
    if (ctrl.writeGroupTitleRid !== writeGroupRid) return;
    ctrl.writeGroupTitleLoading = false;
    ctrl.writeGroupTitleLoadingRid = null;
    ctrl.writeGroupTitleError = error && error.error ? error.error : 'Unable to load multi-point write header';
    m.redraw();
  });
}

function saveWriteGroupTitle(ctrl, writeGroupRid) {
  if (!writeGroupRid || !ctrl.writeGroupTitleDirty) {
    return Promise.resolve();
  }

  var title = writeGroupTitleValue(ctrl.writeGroupTitle);
  return loaders.requestWithLoader({
    loader: 'submit',
    method: 'PUT',
    url: '/api/v1/dataset/' + normalizeRidForUrl(writeGroupRid),
    body: {
      writeGroupTitle: title
    }
  })
  .then(function (dataset) {
    var savedTitle = writeGroupTitleValue(dataset && dataset.writeGroupTitle);
    ctrl.writeGroupTitle = savedTitle;
    ctrl.writeGroupTitleOriginal = savedTitle;
    ctrl.writeGroupTitleDirty = false;
    return dataset;
  });
}

module.exports = {
  oninit: function () {
    var ctrl = this;
    ctrl.unitOptions = [];
    ctrl.unitOptionKinds = {};
    ctrl.unitPrefixSymbols = [];
    ctrl.unitLoadError = null;

    var unitsRequest = loaders.requestWithLoader({
      loader: 'background',
      method: 'GET',
      url: '/api/v1/unit',
    });
    var prefixRequest = loaders.requestWithLoader({
      loader: 'background',
      method: 'GET',
      url: '/api/v1/unit/prefixes',
    });

    Promise.all([unitsRequest, prefixRequest])
      .then(function (results) {
        var units = results[0] || [];
        var prefixes = results[1] || [];
        var built = buildUnitOptions(units, prefixes);
        ctrl.unitOptions = built.options;
        ctrl.unitOptionKinds = built.optionKinds;
        ctrl.unitPrefixSymbols = built.prefixSymbols;
      })
      .catch(function (err) {
        ctrl.unitLoadError = err && err.message ? err.message : 'Failed to load units';
        console.error(ctrl.unitLoadError);
      })
      .finally(function () {
        m.redraw();
      });
  },
  view: function (vnode) {
    var ctrl = this
    ctrl.parentCtrl = vnode.attrs.parentCtrl;
    var isAggregate = isAggregatePoint(ctrl.parentCtrl.point());
    if (!ctrl.unitPickerState) {
      ctrl.unitPickerState = {};
    }
    var writeGroupOptions = buildWriteGroupOptions(ctrl.parentCtrl.point(), Point.form.writeGroup());
    var selectedWriteGroup = Point.form.writeGroup();
    loadWriteGroupTitle(ctrl, selectedWriteGroup);
    var submitConfiguration = function () {
      if (isAggregate) {
        Point.form.writable(0);
      }
      return Point.save()
      .then(function (savedPoint) {
        return saveWriteGroupTitle(ctrl, selectedWriteGroup)
        .then(function () {
          return savedPoint;
        });
      })
      .catch(function (error) {
        if (error && error.error && typeof error.error === 'string') {
          Point.errors = [error.error];
        }
        m.redraw();
        return Promise.reject(error);
      });
    };

    function unitPicker(valueGetter, fieldKey) {
      if (!ctrl.unitPickerState[fieldKey]) {
        ctrl.unitPickerState[fieldKey] = { active: false };
      }
      var state = ctrl.unitPickerState[fieldKey];
      var currentValue = valueGetter();
      var suggestions = state.active
        ? filterUnitOptions(ctrl.unitOptions, ctrl.unitOptionKinds, currentValue, MAX_SUGGESTIONS)
        : [];

      return m("form", [
        m("div.mb2.relative",[
          m("input", {
            type: 'text',
            autocomplete: 'off',
            value: currentValue, 
            oninput: function (ev) { valueGetter(ev.target.value) },
            onfocus: function () { state.active = true; },
            onblur: function () { state.active = false; }
          }),
          state.active && suggestions.length
            ? m('div.mt1.ba.bg-dark-gray', {
              style: {
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 5,
                maxHeight: (VISIBLE_SUGGESTIONS * 2) + 'rem',
                overflowY: 'auto'
              }
            }, suggestions.map(function (unit) {
              return m('div.pa2.pointer.hover-bg-mid-gray', {
                onmousedown: function (ev) {
                  ev.preventDefault();
                  valueGetter(unit.value);
                  state.active = false;
                }
              }, formatOptionLabel(unit.value, unit.kind, ctrl.unitPrefixSymbols));
            }))
            : null
        ])
      ]);
    }

    return m('div', [
      isAggregate ? m('p.yellow.f6.mb2', 'Aggregate points are calculated values. Manual writes, writable, and overrides are disabled.') : null,
      m(headingElement, {label: "Writable"}),
      m("div.mb2",[
        m("form", [
          m("select.form-control", {
            value: isAggregate ? 0 : Point.form.writable(),
            disabled: isAggregate,
            onchange: function (ev) {
              if (isAggregate) return;
              Point.form.writable(ev.target.value);
            }
          }, [
            m("option", {value:0}, 'False'),
            m("option", {value:1}, 'True')
          ])
        ])
      ]),
      m(headingElement, {label: "Multi-Point Write Dataset"}),
      writeGroupOptions.length > 0 ?
        m("form", [
          m("div.mb1", [
            m("select.form-control", {
              id: 'write-group-input',
              value: Point.form.writeGroup() || '',
              onchange: function (ev) {
                Point.form.writeGroup(ev.target.value || null);
                loadWriteGroupTitle(ctrl, ev.target.value || null);
              }
            }, [
              m("option", {value: ''}, 'None'),
              writeGroupOptions.map(function (option) {
                return m("option", {value: option.value}, option.label);
              })
            ])
          ]),
          selectedWriteGroup ? [
            m(headingElement, {label: "Multi-Point Write Header"}),
            m("div.mb1", [
              m("input", {
                id: 'write-group-title-input',
                type: 'text',
                value: ctrl.writeGroupTitle || '',
                placeholder: DEFAULT_MULTI_POINT_WRITE_TITLE,
                disabled: ctrl.writeGroupTitleLoading,
                oninput: function (ev) {
                  ctrl.writeGroupTitle = ev.target.value;
                  ctrl.writeGroupTitleDirty = writeGroupTitleValue(ctrl.writeGroupTitle) !== ctrl.writeGroupTitleOriginal;
                }
              })
            ]),
            ctrl.writeGroupTitleError ? m('p.f6.red.mt0.mb2', ctrl.writeGroupTitleError) : null
          ] : null,
          m('p.f6.silver.mt0.mb2', 'Choose a dataset that already uses this point.')
        ]) :
        m('p.f6.silver.mt0.mb2', 'Add this point to a dashboard dataset before configuring a multi-point write.'),
      m(headingElement, {
        label: "Unit",
        helpIcon: true,
        helpFileName: 'unitConversions.md',
        helpModalTitle: 'Scaling and Unit Conversion',
      }, 
        m('p', {'data-tooltip': 'Test 12342345'})
      ),    
      unitPicker(Point.form.unit, 'unit'),
      m(headingElement, {label: "Unit Conversion"}),    
      unitPicker(Point.form.destUnit, 'destUnit'),
      m(headingElement, {label: "Display Unit"}),
      unitPicker(Point.form.displayUnit, 'displayUnit'),
      m(headingElement, {
        label: "Format",
        helpIcon: true,
        helpFileName: 'numberFormatting.md',
        helpModalTitle: 'Number Formatting',
      }),
      m("form", [
        m("div.mb2",[
          m("input", {
            type: 'text',
            value: Point.form.format(), 
            oninput: function (ev) { Point.form.format(ev.target.value)}
          })
        ])
      ]),
      m(headingElement, {label: "Scale"}),    
      m("form", [
        m("div.mb2",[
          m("input", {
            type: 'number',
            value: Point.form.scale(), 
            oninput: function (ev) { Point.form.scale(ev.target.value)}
          })
        ])
      ]),
      ctrl.parentCtrl.point().asset ? ''
      :
        m('div',[
          m(headingElement, {label: "Offset"}),    
          m("form", [
            m("div.mb2",[
              m("input", {
                type: 'number',
                value: Point.form.offset(), 
                step: ".01",
                oninput: function (ev) { Point.form.offset(ev.target.value)}
              })
            ])
          ])
        ]),
      m("form", [
        m("div.mb2",[
            m(enumerationEditor, {
              point: vnode.attrs.parentCtrl.point,
            })
        ])
      ]),
      // m.component(headingElement, {label: "Increment"}),    
      // m("form", [
      //   m("div.mb2",[
      //       m("input", {
      //         type: 'number',
      //         value: Point.form.increment(), 
      //         oninput: m.withAttr('value', Point.form.increment)}, [
      //       ])
      //   ])
      // ]),
      m(headingElement, {label: "Valid High Range"}),
      m("form", [
        m("div.mb2",[
            m("input", {
              type: 'number',
              value: Point.form.validHiLimit(), 
              oninput: function (ev) { Point.form.validHiLimit(ev.target.value)}
            })
        ])
      ]),
      m(headingElement, {label: "Valid Low Range"}),
      m("form", [
        m("div.mb2",[
            m("input", {
              type: 'number',
              value: Point.form.validLoLimit(), 
              oninput: function (ev) { Point.form.validLoLimit(ev.target.value)}
            })
        ])
      ]),
      Point.errorView(),
      m(buttonElement, {
        label: 'Submit',
        func: submitConfiguration,
        class: 'w4 fr'
      })
    ]);
  }
}
